import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, Loader2, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { T } from "@/constants/testIds";
import { FadeUp, Eyebrow } from "@/components/motion";
import Seo from "@/components/Seo";
import TiltCover from "@/components/TiltCover";

export default function Books() {
  const [buyingKey, setBuyingKey] = useState("");
  const { data: books, isLoading } = useQuery({
    queryKey: ["books"],
    queryFn: async () => (await api.get("/books")).data,
  });

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-32 sm:px-8 lg:px-14 lg:pt-40">
      <Seo
        title="The Miranda Trilogy — Books | Isabella MacLeod"
        description="Browse Isabella MacLeod's trilogy: Metamorphosis (2015), Nano (2019), and Transhuman (2023) — with direct checkout and Amazon.com / Amazon.ca links."
        path="/books"
      />
      <FadeUp className="mb-20 max-w-3xl">
        <Eyebrow className="mb-6">The Catalog</Eyebrow>
        <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
          The Miranda <span className="serif-italic text-cyanx">Trilogy</span>
        </h1>
        <p className="mt-7 max-w-xl text-base font-light leading-relaxed text-dim sm:text-lg">
          Follow Miranda — nurse, mother, survivor — across three books: from a marriage
          falling apart in Nova Scotia to a global conspiracy that reaches into the body itself.
        </p>
      </FadeUp>

      {isLoading && (
        <div className="py-20 text-center font-mono text-xs uppercase tracking-[0.3em] text-mute2">
          Loading catalog…
        </div>
      )}

      <div className="space-y-28">
        {(books || []).map((b, i) => (
          <FadeUp key={b.slug} testid={T.bookCard(b.slug)}>
            <article
              className={`grid items-center gap-12 lg:grid-cols-12 lg:gap-16 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-5">
                <div className="mx-auto max-w-[380px]">
                  <TiltCover
                    src={b.cover}
                    alt={`${b.title} — cover art`}
                    glow={i === 1 ? "255,107,53" : "0,240,255"}
                  />
                </div>
              </div>

              <div className="lg:col-span-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute2">
                  Book {String(b.series_order).padStart(2, "0")} — {b.year} — {b.publisher}
                </p>
                <h2 className="mt-4 font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl lg:text-6xl">
                  <Link to={`/books/${b.slug}`} className="transition-colors hover:text-cyanx" data-testid={T.bookLink(b.slug)}>
                    {b.title}
                  </Link>
                </h2>
                {b.award && (
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ember">
                    {b.award}
                  </p>
                )}
                <p className="mt-6 line-clamp-4 max-w-xl text-base font-light leading-relaxed text-dim">
                  {b.synopsis.split("\n\n")[0]}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <span className="font-serif text-2xl italic text-cyanx">{b.price_label}</span>
                  <Link
                    to={`/books/${b.slug}`}
                    data-testid={`books-read-more-${b.slug}`}
                    className="group flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:border-cyanx/60 hover:text-cyanx"
                  >
                    Read more
                    <ArrowRight_SMALL />
                  </Link>
                  {b.stripe_lookup_key && (
                    <button
                      onClick={async () => {
                        try {
                          setBuyingKey(b.stripe_lookup_key);
                          await startCheckout(b.stripe_lookup_key);
                        } catch {
                          setBuyingKey("");
                          toast.error("Could not start checkout. Please try again.");
                        }
                      }}
                      disabled={buyingKey === b.stripe_lookup_key}
                      data-testid={`buy-direct-${b.slug}`}
                      className="group flex items-center gap-2 rounded-full bg-cyanx px-6 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-void transition-all duration-300 hover:brightness-110 disabled:opacity-60"
                    >
                      {buyingKey === b.stripe_lookup_key ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <>
                          <ShoppingCart size={13} />
                          Buy direct — {b.direct_price_label}
                        </>
                      )}
                    </button>
                  )}
                  <a
                    href={b.amazon_com}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={T.buyAmazonCom(b.slug)}
                    className="group flex items-center gap-2 rounded-full bg-ink px-6 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-void transition-colors duration-300 hover:bg-cyanx"
                  >
                    Buy on Amazon
                    <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </a>
                </div>
              </div>
            </article>
          </FadeUp>
        ))}
      </div>

      <FadeUp className="mt-24">
        <Link
          to="/"
          data-testid="books-back-home-link"
          className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-mute2 transition-colors hover:text-cyanx"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back home
        </Link>
      </FadeUp>
    </div>
  );
}

const ArrowRight_SMALL = () => (
  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
);
